import { isFunction } from 'lodash'
import moment from 'moment'

import { getIdOrNull, isNonEmptyArray, requestMultipart, requestWithPromise } from '..'
import { HttpMethods, makeQueryStringFromObject, makeQueryString } from '../apiHelper'
import { DATE_WITHOUT_TIME, LanguageTags } from '../constants'
import { TYPES } from '../entities/internshipDocument'
import { downloadSpreadsheet, getDocument, PeriodExportParameters } from './export' //eslint-disable-line
import apiClient from './apiClient'
import Clonable from '../entities/clonable'
import { DEFAULT_INTERNSHIP_FILTERS } from '../../Providers/GlobalFiltersProvider'
import { OwnershipFilters } from '../../Components/shared/InternshipsManager/utils/constants'

const EXPORT_HOURS_PARAMETERS = { endDate: moment(), sections: [], startDate: moment() }

const ExportTypes = Object.freeze({
  LIST: 'list',
  CALENDAR: 'calendar'
})

const ErrorMessages = Object.freeze({
  EXPORT_FAILED: 'EXPORT_FAILED'
})

export class InternshipFilters extends Clonable {
  constructor ({ period = null, institutions = null, schools = null, sections = null, sectors = null, states = null, areStartedExcluded = false, ownership = OwnershipFilters.ALL } = DEFAULT_INTERNSHIP_FILTERS) {
    super()
    this.period = period
    this.institutions = isNonEmptyArray(institutions) ? institutions.map(getIdOrNull) : null
    this.schools = isNonEmptyArray(schools) ? schools.map(getIdOrNull) : null
    this.sections = isNonEmptyArray(sections) ? sections.map(getIdOrNull) : null
    this.sectors = isNonEmptyArray(sectors) ? sectors.map(getIdOrNull) : null
    this.states = isNonEmptyArray(states) ? states.map(getIdOrNull) : null
    this.areStartedExcluded = areStartedExcluded
    this.ownership = ownership
  }
}

/** @enum {string} */
export const SerializationContexts = Object.freeze({
  INTERNSHIP: 'internship',
  SCHOOL: 'school',
  NOTES: 'notes'
})

export const acceptInternship = (user, internship) => {
  return requestWithPromise('/internship/accept',
    HttpMethods.POST,
    { internship: getIdOrNull(internship) },
    user
  )
}

export const cancelInternship = (user, internship) => {
  return requestWithPromise('/internship/cancel',
    HttpMethods.POST,
    { internship: getIdOrNull(internship) },
    user
  )
}

export const deleteInternship = (user, internship) => {
  return apiClient.delete(user, `api/internships/${getIdOrNull(internship)}`, null)
}

export const deleteInternships = (user, internships) => apiClient.delete(user, 'api/internships', { internships })

export const duplicateInternships = (user, internships = [], offset = 0) => {
  return apiClient.post(user, 'internships/duplicate', { internships, offset })
}

export const duplicateInternship = (internship, user) => {
  return requestWithPromise(
    '/internship/duplicate',
    HttpMethods.POST,
    { internship: getIdOrNull(internship) },
    user
  )
}

export const getAllInternships = (user, queryParameters) => apiClient.get(user, 'api/internships', queryParameters)

export const preAccept = (user, internship) => {
  return requestWithPromise(`/internship/${getIdOrNull(internship)}/pre-accept`,
    HttpMethods.PATCH,
    {},
    user
  )
}

export const refuseInternship = (user, internship, reasons, comment) => {
  return requestWithPromise('/internship/refuse',
    HttpMethods.POST,
    { internship: getIdOrNull(internship), reasons, comment },
    user
  )
}

export const resetToCreated = (user, internship) => {
  return requestWithPromise(`/internship/${getIdOrNull(internship)}/created`,
    HttpMethods.PATCH,
    {},
    user
  )
}

export const resetToPending = (user, internship) => apiClient.post(user, 'internship/reset-state', { internship: getIdOrNull(internship) })

/**
 * @deprecated 2025-05-27
 * @see {updateInternship}
 */
export const updateInternshipOld = (internship, body, user) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}`,
    HttpMethods.PATCH,
    body,
    user
  )
}

export const updateInternship = (user, internship, headers) => apiClient.patch(user, `api/internships/${getIdOrNull(internship)}`, internship, headers)
export const createInternships = (user, internships, headers) => apiClient.post(user, 'api/internships', internships, headers)

export const oldCreateInternship = (internshipData, user) => {
  return requestWithPromise(
    '/api/internship',
    HttpMethods.POST,
    internshipData,
    user
  )
}

export const linkInternshipToAct = (internship, act, user, customErrorHandling = false) => {
  return requestWithPromise(
    `/api/internships/${internship}/act-types`,
    HttpMethods.POST,
    { actType: act },
    user,
    true,
    customErrorHandling
  )
}

export const unlinkInternshipFromAct = (internship, act, user) => {
  return requestWithPromise(
    `/api/internships/${internship}/act-types/${act}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const getLinkedActs = (internship, user) => {
  return requestWithPromise(
    `/api/internships/${internship}/act-types`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getInternshipShifts = (internship, user, parameters = {}) => {
  return apiClient.get(user, `api/internships/${getIdOrNull(internship)}/shifts`, parameters)
}

export const uploadInternshipConvention = (internship, convention, user) => {
  const body = {
    'document[internship]': getIdOrNull(internship),
    file: convention,
    type: TYPES.CONVENTION
  }

  return requestMultipart(
    '/internship/document/upload',
    HttpMethods.POST,
    body,
    user
  )
}

export const uploadInternshipDocument = (user, internship, file) => {
  return requestMultipart(
    '/internship/document/upload',
    HttpMethods.POST,
    {
      'document[internship]': getIdOrNull(internship),
      file,
      type: TYPES.DOCUMENT
    },
    user
  )
}

export const getTotalHoursActsByInternship = (internship, user) => {
  return requestWithPromise(
    `/internship/${getIdOrNull(internship)}/schedule`,
    HttpMethods.GET,
    null,
    user
  ).then(json => {
    return json?.data ?? {}
  })
}

export const addInternshipScheduleComplements = (internship, scheduleComplement, user) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/schedule-complements`,
    HttpMethods.POST,
    scheduleComplement.toRequestBody(),
    user
  )
}

export const getScheduleComplements = (internship, user) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/schedule-complements`,
    HttpMethods.GET,
    null,
    user
  )
}

export const deleteScheduleComplement = (internship, scheduleComplement, user) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/schedule-complements/${getIdOrNull(scheduleComplement)}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const updateInternshipScheduleComplement = (internship, scheduleComplement, user) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/schedule-complements/${getIdOrNull(scheduleComplement)}`,
    HttpMethods.PATCH,
    scheduleComplement.toRequestBody(),
    user
  )
}

export const getInternshipDocuments = (user, internship) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/documents`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getInternshipInternshipYearActTypes = (internship, user, parameters = {}) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/internship-year/act-types${makeQueryStringFromObject(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getRelatedInternships = (internship, user, parameters) => {
  return requestWithPromise(
    `/internship/${getIdOrNull(internship)}/related-internships${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const validateInternship = (user, internship) => {
  return apiClient.post(user, 'internship/validate-schedule', { internship: getIdOrNull(internship) })
}

export const invalidateInternship = (user, internship) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/invalidate`,
    HttpMethods.PATCH,
    {},
    user
  )
}

export const validateInternshipDesideratas = (user, internship) => {
  return requestWithPromise(
    `/internship/${getIdOrNull(internship)}/shifts/desiderata`,
    HttpMethods.PATCH,
    { isDesiderata: false },
    user
  )
}

/** @param {PeriodExportParameters} paramaters */
export const exportInternshipsByPeriod = (user, parameters) => {
  return apiClient.post(user, 'internship/export-internships-daterange', parameters)
}

export const exportInternshipsByPeriodTivoli = (user, parameters) => {
  return apiClient.post(user, 'export/internships-reporting/custom/tivoli', parameters)
}

export const getInternshipsByPeriod = (user, parameters) => apiClient.get(user, 'api/internships/date-range', parameters)

export const updateBulkIsDraft = (user, internshipIds, isDraft) =>
  apiClient.patch(user, 'api/internships', { ids: internshipIds, props: { isDraft } })

export const updateBulkIsDraftSchedules = (user, internshipIds, isDraftSchedules) =>
  apiClient.patch(user, 'api/internships', { ids: internshipIds, props: { isDraftSchedules } })

export const deleteInternshipShifts = (user, internship) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/shifts`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const deleteInternshipsShifts = (user, internshipIds) => apiClient.delete(user, 'internship/shifts', { ids: internshipIds })

export const exportTimeline = (user, parameters) => apiClient.post(user, 'internship/export-timeline', parameters)

const DEFAULT_IMPORT_PARAMETER = { school: null, language: LanguageTags.EN }
export const importInternships = (user, file, parameters = DEFAULT_IMPORT_PARAMETER, onSuccess = undefined, onError = undefined, refreshRate = undefined) => {
  return requestMultipart(
    '/import/internships',
    HttpMethods.POST,
    { ...parameters, file },
    user
  ).then(json => {
    if (json?.job) {
      return getDocument(json.job, user, onError, onSuccess, refreshRate)
    } else if (isFunction(onError)) {
      onError()
    }
  })
}

export const getInternshipsImportTemplate = (user, parameters = DEFAULT_IMPORT_PARAMETER) => {
  return apiClient.get(user, 'import/internships/template', parameters).then(json => {
    if (json?.data) {
      return downloadSpreadsheet(json.data, user)
    }

    throw new Error(ErrorMessages.EXPORT_FAILED)
  })
}

export const getInternshipEvaluation = (user, internship) => {
  return requestWithPromise(
    `/evaluation/internship/${getIdOrNull(internship)}`,
    HttpMethods.GET,
    null,
    user
  )
}

const DEFAULT_PARAM_INTERNSHIP_WITHOUT_STUDENT = { endDate: moment().format(DATE_WITHOUT_TIME), institution: -1, section: -1, school: -1, startDate: moment().format(DATE_WITHOUT_TIME) }
export const createInternshipWithoutStudent = (user, internship = DEFAULT_PARAM_INTERNSHIP_WITHOUT_STUDENT) => {
  return apiClient.post(user, 'internship/create-without-student', internship)
}

export const getInternshipSupervisors = (user, internship) => {
  return requestWithPromise(
    `/api/internships/${getIdOrNull(internship)}/supervisors`,
    HttpMethods.GET,
    null,
    user
  )
}

const EXPORT_INTERNSHIP_PARAMETERS = {
  startDate: moment(),
  endDate: moment(),
  filters: {},
  type: ExportTypes.LIST
}

export const exportInternships = (user, parameters = EXPORT_INTERNSHIP_PARAMETERS) => apiClient.post(user, 'export/internships', parameters)
export const exportStudentsHours = (user, parameters = EXPORT_HOURS_PARAMETERS) => apiClient.post(user, 'export/internships/student-hours', parameters)
export const getInternship = (user, internship, parameters = {}) => apiClient.get(user, `internship/details/${getIdOrNull(internship)}`, parameters)
